import angular from 'angular';
import { platformBrowserDynamic } from 'angular-ts-decorators';
import 'reflect-metadata';

import { setCompatibilityGuideApiConfig } from '@gn-audio/core/api/compatibility-guide/config/actions';
import { setKnowledgeBaseApiConfig } from '@gn-audio/core/api/knowledge-base/config/actions';
import { setMarketingDocumentsApiConfig } from '@gn-audio/core/api/marketing-documents/config/actions';
import { setPricingApiConfig } from '@gn-audio/core/api/pricing/config/actions';
import { setProductCatalogueApiConfig } from '@gn-audio/core/api/product-catalogue/config/actions';
import { setRetailersApiConfig } from '@gn-audio/core/api/retailers/config/actions';
import { setSiteSettingsApiConfig } from '@gn-audio/core/api/site-settings/config/actions';
import { setStoreSupportApiConfig } from '@gn-audio/core/api/store-support/config/actions';
import { fetchGet } from '@gn-audio/core/common';
import { isInIframe } from '@gn-audio/core/common/utilities';
import { bootstrapSFCCBasketIfPossible } from '@gn-audio/sfcc-basket/utilities';

import {
  bootstrapDRBasketIfPossible
} from './src/Feature/Navigation/platform/app/Cart/utilities/bootstrap-dr-basket-if-possible';
import { CustomEvent } from './src/Feature/Shared/platform/app/enums';
import { bootstrapAuthApis, bootstrapUipIfPossible } from './src/Feature/Uip/platform/app/utilities';
import { IProductApiConfig } from './src/Foundation/ProductApi/platform';
import { ShellModule } from './src/Project/Shell/platform';

(async () => {
  const locale = document.documentElement.dataset.marketLocale;
  const productApiConfigUrl = '/product-api/config?marketLocale=' + locale;
  const productApiConfig: IProductApiConfig | null = await fetchGet<IProductApiConfig>(
      productApiConfigUrl,
      {},
      'same-origin',
  );
  if (!productApiConfig) {
    console.error('APIs are not configured');
    return;
  }

  setSiteSettingsApiConfig({
    baseUrl: productApiConfig.settingsUrl,
    isExternalContent: (location.pathname || '').toLowerCase().split('/').indexOf('externalcontent') > -1,
    marketLocale: productApiConfig.language,
    siteContext: productApiConfig.siteContext,
  });

  setCompatibilityGuideApiConfig({
    baseUrl: productApiConfig.compatibilityGuideUrl,
    marketLocale: productApiConfig.language,
  });

  setKnowledgeBaseApiConfig({
    baseUrl: productApiConfig.knowledgeBaseUrl,
    marketLocale: productApiConfig.language,
  });

  setStoreSupportApiConfig({
    baseUrl: productApiConfig.storeSupportUrl,
    marketLocale: productApiConfig.language,
    siteContext: productApiConfig.siteContext,
  });

  setMarketingDocumentsApiConfig({
    baseUrl: productApiConfig.marketingDocumentsUrl,
    marketLocale: productApiConfig.language,
  });

  setRetailersApiConfig({
    baseUrl: productApiConfig.retailersUrl,
    marketLocale: productApiConfig.language,
  });

  await setProductCatalogueApiConfig({
    apiKey: productApiConfig.productCatalogueApiKey,
    baseUrl: productApiConfig.productCatalogueUrl,
    marketLocale: productApiConfig.language,
  });

  await setPricingApiConfig({
    baseUrl: productApiConfig.pricingUrl,
    marketLocale: productApiConfig.language,
  });

  // This is a temporary solution to make top menu scripts work
  window.dispatchEvent(new Event(CustomEvent.ApiConfigured));

  // No need to bootstrap baskets and UIP for iframes
  if (!isInIframe()) {
    await bootstrapAuthApis(productApiConfig);
    await bootstrapSFCCBasketIfPossible();
    await bootstrapDRBasketIfPossible();
    await bootstrapUipIfPossible();
  }

  angular.module('gn.audio.public.productapi')
      .constant('productApiConfig', productApiConfig);
  platformBrowserDynamic()
      .bootstrapModule(ShellModule, { strictDi: true });

  // Emit event which ensures all components are rendered, because 'load' doesn't ensure this
  window.dispatchEvent(new Event(CustomEvent.ShellModuleRendered));
})();
